/**
 * API Configuration
 * Central configuration for API endpoints and settings
 */

// API Base URL - Change based on environment
// @ts-ignore - Vite env types
export const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8000';

// API Version
export const API_VERSION = 'api';

// Full API URL
export const API_URL = `${API_BASE_URL}/${API_VERSION}`;

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login/',
    REFRESH: '/auth/refresh/',
    VERIFY: '/auth/verify/',
    PROFILE: '/auth/users/profile/',
    CHANGE_PASSWORD: '/auth/users/change_password/',
  },
  
  // Companies
  COMPANIES: {
    BASE: '/companies/',
    DETAIL: (id: string) => `/companies/${id}/`,
    STATS: (id: string) => `/companies/${id}/stats/`,
    UPGRADE_PLAN: (id: string) => `/companies/${id}/upgrade_plan/`,
    ENTITLEMENTS: (id: string) => `/companies/${id}/entitlements/`,
    CHECK_MODULE: (id: string) => `/companies/${id}/check_module/`,
    GRANT_MODULE: (id: string) => `/companies/${id}/grant_module/`,
    REVOKE_MODULE: (id: string) => `/companies/${id}/revoke_module/`,
  },

  // Modules (Platform Features/Entitlements)
  MODULES: {
    BASE: '/modules/',
    DETAIL: (id: string) => `/modules/${id}/`,
    BY_CATEGORY: '/modules/by_category/',
    FOR_TIER: '/modules/for_tier/',
    MY_ENTITLEMENTS: '/modules/my-entitlements/',
  },
  
  // Markets
  MARKETS: {
    BASE: '/markets/',
    DETAIL: (id: string) => `/markets/${id}/`,
    STATS: (id: string) => `/markets/${id}/stats/`,
    ADD_SUBSIDY_PLAN: (id: string) => `/markets/${id}/add_subsidy_plan/`,
    ADD_CITY: (id: string) => `/markets/${id}/add_city/`,
    SUBSIDY_PLANS: '/markets/subsidy-plans/',
    SUBSIDY_PLAN_DETAIL: (id: string) => `/markets/subsidy-plans/${id}/`,
    CITIES: '/markets/cities/',
    CITY_DETAIL: (id: string) => `/markets/cities/${id}/`,
  },
  
  // Users
  USERS: {
    BASE: '/auth/users/',
    DETAIL: (id: string) => `/auth/users/${id}/`,
    PROFILE: '/auth/users/profile/',
    CHANGE_PASSWORD: '/auth/users/change_password/',
    PERFORMANCE: (id: string) => `/auth/users/${id}/performance/`,
    DEACTIVATE: (id: string) => `/auth/users/${id}/deactivate/`,
    ACTIVATE: (id: string) => `/auth/users/${id}/activate/`,
  },
  
  // Projects
  PROJECTS: {
    BASE: '/projects/',
    DETAIL: (id: string) => `/projects/${id}/`,
    UNITS: (id: string) => `/projects/${id}/units/`,
    STATS: (id: string) => `/projects/${id}/stats/`,
    AVAILABLE_UNITS: (id: string) => `/projects/${id}/available_units/`,
    AMENITIES: (id: string) => `/projects/${id}/amenities/`,
    FEATURED: '/projects/featured/',
    BY_LOCATION: '/projects/by_location/',
    
    // Stages
    STAGES: '/projects/stages/',
    STAGE_DETAIL: (id: string) => `/projects/stages/${id}/`,
    STAGE_BLOCKS: (id: string) => `/projects/stages/${id}/blocks/`,
    STAGE_TYPOLOGIES: (id: string) => `/projects/stages/${id}/typologies/`,
    STAGE_AMENITIES: (id: string) => `/projects/stages/${id}/amenities/`,
    STAGE_PRICING_SCHEMES: (id: string) => `/projects/stages/${id}/pricing_schemes/`,
    
    // Blocks
    BLOCKS: '/projects/blocks/',
    BLOCK_DETAIL: (id: string) => `/projects/blocks/${id}/`,
    BLOCK_UNITS: (id: string) => `/projects/blocks/${id}/units/`,
    BLOCK_AMENITIES: (id: string) => `/projects/blocks/${id}/amenities/`,
    
    // Typologies
    TYPOLOGIES: '/projects/typologies/',
    TYPOLOGY_DETAIL: (id: string) => `/projects/typologies/${id}/`,
    TYPOLOGY_UNITS: (id: string) => `/projects/typologies/${id}/units/`,
    
    // Amenities
    AMENITIES_LIST: '/projects/amenities/',
    AMENITY_DETAIL: (id: string) => `/projects/amenities/${id}/`,
    
    // Orbit Views
    ORBIT_VIEWS: '/projects/orbit-views/',
    ORBIT_VIEW_DETAIL: (id: string) => `/projects/orbit-views/${id}/`,

    // Pricing Schemes (Price Lists)
    PRICING_SCHEMES: '/projects/pricing-schemes/',
    PRICING_SCHEME_DETAIL: (id: string) => `/projects/pricing-schemes/${id}/`,

    // Units
    UNITS_BASE: '/projects/units/',
    UNIT_DETAIL: (id: string) => `/projects/units/${id}/`,
    UNIT_RESERVE: (id: string) => `/projects/units/${id}/reserve/`,
    UNIT_MARK_AS_SOLD: (id: string) => `/projects/units/${id}/mark_as_sold/`,
    UNIT_PRE_CHECK_SOLD: (id: string) => `/projects/units/${id}/pre-check-sold/`,
    UNIT_SIMILAR: '/projects/units/similar/',
  },

  // Leads
  LEADS: {
    BASE: '/leads/',
    DETAIL: (id: string) => `/leads/${id}/`,
    ASSIGN: (id: string) => `/leads/${id}/assign/`,
    CHANGE_STATUS: (id: string) => `/leads/${id}/change_status/`,
    UPDATE_SCORE: (id: string) => `/leads/${id}/update_score/`,
    SCORE_BREAKDOWN: (id: string) => `/leads/${id}/score-breakdown/`,
    RECALCULATE_SCORE: (id: string) => `/leads/${id}/recalculate-score/`,
    ADD_NOTE: (id: string) => `/leads/${id}/add_note/`,
    TIMELINE: (id: string) => `/leads/${id}/timeline/`,
    SCORING_SCATTER: '/leads/scoring-scatter/',
    STATS: '/leads/stats/',
    HOT_LEADS: '/leads/hot_leads/',
    PRIORITY_LEADS: '/leads/priority_leads/',
    DUPLICATES: '/leads/duplicates/',
    UPCOMING_FOLLOWUPS: '/leads/upcoming_followups/',
    OVERDUE_FOLLOWUPS: '/leads/overdue_followups/',
    BULK_ASSIGN: '/leads/bulk_assign/',
    BULK_STATUS_CHANGE: '/leads/bulk_status_change/',
    BULK_DELETE: '/leads/bulk_delete/',
    BULK_HARD_DELETE: '/leads/bulk_hard_delete/',
    BULK_STAGE_CHANGE: '/leads/bulk_stage_change/',
    PERFORMANCE_BY_SOURCE: '/leads/performance_by_source/',

    // Lead Unification
    UNIFY: (id: string) => `/leads/${id}/unify/`,

    // Lead Transfers
    TRANSFER_TO_PROJECT: (id: string) => `/leads/${id}/transfer_to_project/`,
    TRANSFER_HISTORY: (id: string) => `/leads/${id}/transfer_history/`,
    TRANSFER_REASONS: '/leads/transfer_reasons/',

    // Lead to Client Conversion
    CONVERT_TO_CLIENT: (id: string) => `/leads/${id}/convert_to_client/`,
    CONVERSION_STATUS: (id: string) => `/leads/${id}/conversion_status/`,

    // Status History
    STATUS_HISTORY: (id: string) => `/leads/${id}/status-history/`,

    // Change Log (field-level audit trail)
    CHANGE_LOG: (id: string) => `/leads/${id}/change-log/`,

    // Lead Sources (taxonomy)
    SOURCES: {
      BASE: '/leads/sources/',
      DETAIL: (id: string) => `/leads/sources/${id}/`,
      TOGGLE_ACTIVE: (id: string) => `/leads/sources/${id}/toggle-active/`,
      TOGGLE_PROJECT_ACTIVE: (id: string) => `/leads/sources/${id}/toggle-project-active/`,
      TOGGLE_PLATFORM_ENABLED: (id: string) => `/leads/sources/${id}/toggle-platform-enabled/`,
      BULK_TOGGLE_PLATFORM: '/leads/sources/bulk-toggle-platform-enabled/',
      BULK_TOGGLE_ACTIVE: '/leads/sources/bulk-toggle-active/',
      BULK_TOGGLE_PROJECT_ACTIVE: '/leads/sources/bulk-toggle-project-active/',
    },

    // Contact Methods (how the lead contacted us)
    CONTACT_METHODS: {
      BASE: '/leads/contact-methods/',
    },

    // Lead Routing Configs (organic channels)
    ROUTING_CONFIGS: {
      BASE: '/leads/lead-routing-configs/',
      DETAIL: (id: string) => `/leads/lead-routing-configs/${id}/`,
    },

    // Lead Documents
    DOCUMENTS: {
      LIST: (leadId: string) => `/leads/${leadId}/documents/`,
      DETAIL: (leadId: string, docId: string) => `/leads/${leadId}/documents/${docId}/`,
      DOWNLOAD: (leadId: string, docId: string) => `/leads/${leadId}/documents/${docId}/download/`,
    },

    // Unified Bitacora (timeline)
    BITACORA: (id: string) => `/leads/${id}/bitacora/`,

    // Field choices (centralized options from Django model)
    FIELD_CHOICES: '/leads/field-choices/',
  },

  // Clients
  CLIENTS: {
    BASE: '/clients/',
    DETAIL: (id: string) => `/clients/${id}/`,
    SEND_INVITATION: (id: string) => `/clients/${id}/send_invitation/`,
    DEACTIVATE: (id: string) => `/clients/${id}/deactivate/`,
    PIPELINE_ROADMAP: (id: string) => `/clients/${id}/pipeline_roadmap/`,
    UPDATE_DELIVERY_DATE: (id: string) => `/clients/${id}/update_delivery_date/`,
    TAGS: (id: string) => `/clients/${id}/tags/`,
    ADD_TAG: (id: string) => `/clients/${id}/add_tag/`,
    REMOVE_TAG: (id: string) => `/clients/${id}/remove_tag/`,
    BULK_ASSIGN: '/clients/bulk_assign/',
    BULK_DELETE: '/clients/bulk_delete/',
    BULK_STAGE_CHANGE: '/clients/bulk_stage_change/',
  },

  // Quotes
  QUOTES: {
    BASE: '/quotes/',
    DETAIL: (id: string) => `/quotes/${id}/`,
    SEND: (id: string) => `/quotes/${id}/send/`,
    EMAIL_PREVIEW: (id: string) => `/quotes/${id}/email-preview/`,
    ACCEPT: (id: string) => `/quotes/${id}/accept/`,
    REJECT: (id: string) => `/quotes/${id}/reject/`,
    MARK_VIEWED: (id: string) => `/quotes/${id}/mark_viewed/`,
    STATUS_HISTORY: (id: string) => `/quotes/${id}/status-history/`,
  },

  // Deals
  DEALS: {
    BASE: '/deals/',
    DETAIL: (id: string) => `/deals/${id}/`,
    MARK_LOST: (id: string) => `/deals/${id}/mark-lost/`,
    MARK_WON: (id: string) => `/deals/${id}/mark-won/`,
    REACTIVATE: (id: string) => `/deals/${id}/reactivate/`,
    CHANGE_LOG: (id: string) => `/deals/${id}/change-log/`,
    TOGGLE_PRIMARY: (id: string) => `/deals/${id}/toggle-primary/`,
    PRE_CHECK_WON: (id: string) => `/deals/${id}/pre-check-won/`,
  },

  // Activities
  ACTIVITIES: {
    BASE: '/activities/',
    DETAIL: (id: string) => `/activities/${id}/`,
  },

  // Calendar
  CALENDAR: {
    BASE: '/calendar/events/',
    DETAIL: (id: string) => `/calendar/events/${id}/`,
    RSVP: (id: string) => `/calendar/events/${id}/rsvp/`,
    CHANGE_STATUS: (id: string) => `/calendar/events/${id}/change-status/`,
    COMPLETE: (id: string) => `/calendar/events/${id}/complete/`,
    UPCOMING: '/calendar/events/upcoming/',
    TODAY: '/calendar/events/today/',
    AVAILABILITY: '/calendar/availability/',
    CALENDAR_VIEW: '/calendar/events/calendar-view/',
    LEAD_HISTORY: '/calendar/events/lead-history/',
  },
  
  // Analytics
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard/',
    LEADS: '/analytics/leads/',
    SALES_FUNNEL: '/analytics/sales-funnel/',
    MARKETING_DASHBOARD: '/analytics/marketing-dashboard/',
    MARKETING_DASHBOARD_SYNC: '/analytics/marketing-dashboard/sync/',
    OWLYSALES: {
      PRESIDENTE: '/analytics/owlysales/presidente/',
      COMANDO: '/analytics/owlysales/comando/',
      VENTAS: '/analytics/owlysales/ventas/',
      ADQUISICION: '/analytics/owlysales/adquisicion/',
      INVENTARIO: '/analytics/owlysales/inventario/',
      EQUIPO: '/analytics/owlysales/equipo/',
      POSTVENTAS: '/analytics/owlysales/postventas/',
    },
  },
  
  // Tags
  TAGS: {
    BASE: '/tags/',
    DETAIL: (id: string) => `/tags/${id}/`,
    BY_CATEGORY: '/tags/by_category/',
    POPULAR: '/tags/popular/',
    SEARCH: '/tags/search/',
    GET_OR_CREATE: '/tags/get_or_create/',
    FOR_LEADS: '/tags/for_leads/',
    FOR_CLIENTS: '/tags/for_clients/',
    // Tag Categories
    CATEGORIES: {
      BASE: '/tags/categories/',
      DETAIL: (id: string) => `/tags/categories/${id}/`,
      WITH_TAGS: '/tags/categories/with_tags/',
      INIT_SYSTEM: '/tags/categories/init_system/',
    },
  },

  // Core (Identification Types, Personas, etc.)
  CORE: {
    IDENTIFICATION_TYPES: {
      BASE: '/core/identification-types/',
      DETAIL: (id: string) => `/core/identification-types/${id}/`,
    },
    PERSONAS: {
      BASE: '/core/personas/',
      DETAIL: (id: string) => `/core/personas/${id}/`,
    },
  },

  // Locations (Countries, States, Cities)
  LOCATIONS: {
    COUNTRIES: '/core/locations/countries/',
    STATES: '/core/locations/states/',
    CITIES: '/core/locations/cities/',
  },

  // Marketing — Email Templates
  MARKETING: {
    EMAIL_TEMPLATES: {
      BASE: '/marketing/email-templates/',
      DETAIL: (id: string) => `/marketing/email-templates/${id}/`,
      DUPLICATE: (id: string) => `/marketing/email-templates/${id}/duplicate/`,
      PREVIEW: (id: string) => `/marketing/email-templates/${id}/preview/`,
      SEND_TEST: (id: string) => `/marketing/email-templates/${id}/send-test/`,
    },
  },

  // Health Check
  HEALTH: '/health/',
} as const;

// Request timeout (ms)
export const REQUEST_TIMEOUT = 30000;

// Upload configuration
export const UPLOAD_CONFIG = {
  // Timeouts (ms)
  ASYNC_REQUEST_TIMEOUT: 30000,     // 30s for async requests
  SYNC_REQUEST_TIMEOUT: 300000,     // 5min for sync processing

  // Polling configuration for async processing
  POLL_INTERVAL_MS: 5000,           // Poll every 5 seconds
  MAX_POLL_DURATION_MS: 900000,     // Max 15 minutes of polling (for amenity matching)

  // Computed values
  get MAX_POLLS() {
    return Math.ceil(this.MAX_POLL_DURATION_MS / this.POLL_INTERVAL_MS);
  },
} as const;

// Token storage keys
export const TOKEN_KEYS = {
  ACCESS: 'owly_access_token',
  REFRESH: 'owly_refresh_token',
  USER: 'owly_user',
  FIREBASE: 'firebase_token',
} as const;

// HTTP Status codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

